import React, { useState, useRef, useEffect } from "react";
import SignPad from "../components/document/SignPad";
import { PencilIcon } from "@heroicons/react/24/outline";
import { useAuth } from "../contexts/AuthContext";
import toast from "react-hot-toast";
import api from "../utils/axios";

const Signature = () => {
  const [isSignPad, setIsSignPad] = useState(false);
  const [isStamp, setIsStamp] = useState(false);
  const [isImageSelect, setIsImageSelect] = useState(false);
  const [image, setImage] = useState(null);
  const [imageRef] = useState(useRef(null));
  const [signatureImageRef] = useState(useRef(null));
  const [initialsImageRef] = useState(useRef(null));
  const [signature, setSignature] = useState(null);
  const [defaultSignImg, setDefaultSignImg] = useState(null);
  const [myInitial, setMyInitial] = useState(null);
  const [tempSignature, setTempSignature] = useState(null);
  const [isAutoSign, setIsAutoSign] = useState(false);
  const [isInitial, setIsInitial] = useState(false);
  const [signatureType, setSignatureType] = useState([
    { name: "draw", enabled: true },
    { name: "typed", enabled: true },
  ]);
  const [saveSignCheckbox, setSaveSignCheckbox] = useState({
    isVisible: false,
    signId: "",
  });
  const [currWidgetsDetails, setCurrWidgetsDetails] = useState({});
  const [widgetType, setWidgetType] = useState("signature");
  const { user, updateProfile } = useAuth();

  useEffect(() => {
    api.get(`/user/me/signature`).then((res) => {
      setSignature(res?.data?.data?.signature_base64);
      setMyInitial(res?.data?.data?.initials_base64);
    });
  }, [user]);
  // Function to handle image upload
  const onImageChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];

      // Validate file type
      const allowedTypes = [
        "image/png",
        "image/jpeg",
        "image/jpg",
        "image/gif",
        "image/webp",
      ];
      if (!allowedTypes.includes(file.type)) {
        toast.error(
          "Please select a valid image file (PNG, JPEG, JPG, GIF, or WebP)"
        );
        return;
      }

      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      if (file.size > maxSize) {
        toast.error("Image file size must be less than 5MB");
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.src = e.target.result;
        img.onload = () => {
          setImage({
            src: e.target.result,
            width: img.width,
            height: img.height,
            name: file.name,
            size: file.size,
            type: file.type,
          });
          toast.success("Image uploaded successfully");
        };
        img.onerror = () => {
          toast.error("Error loading image. Please try again.");
        };
      };
      reader.onerror = () => {
        toast.error("Error reading file. Please try again.");
      };
      reader.readAsDataURL(file);
    }
  };

  // Function to handle signature image upload directly
  const onSignatureImageChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];

      // Validate file type
      const allowedTypes = [
        "image/png",
        "image/jpeg",
        "image/jpg",
        "image/gif",
        "image/webp",
      ];
      if (!allowedTypes.includes(file.type)) {
        toast.error(
          "Please select a valid image file (PNG, JPEG, JPG, GIF, or WebP)"
        );
        return;
      }

      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      if (file.size > maxSize) {
        toast.error("Image file size must be less than 5MB");
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setSignature(e.target.result);
        toast.success("Signature image uploaded successfully");
      };
      reader.onerror = () => {
        toast.error("Error reading file. Please try again.");
      };
      reader.readAsDataURL(file);
    }
  };

  // Function to handle initials image upload directly
  const onInitialsImageChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];

      // Validate file type
      const allowedTypes = [
        "image/png",
        "image/jpeg",
        "image/jpg",
        "image/gif",
        "image/webp",
      ];
      if (!allowedTypes.includes(file.type)) {
        toast.error(
          "Please select a valid image file (PNG, JPEG, JPG, GIF, or WebP)"
        );
        return;
      }

      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      if (file.size > maxSize) {
        toast.error("Image file size must be less than 5MB");
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setMyInitial(e.target.result);
        toast.success("Initials image uploaded successfully");
      };
      reader.onerror = () => {
        toast.error("Error reading file. Please try again.");
      };
      reader.readAsDataURL(file);
    }
  };

  // Function to save signature (no API call as requested)
  const saveSign = (type, isDefaultSign, width, height, typedSignature) => {
    // console.log("Signature saved:", {
    //   type,
    //   isDefaultSign,
    //   width,
    //   height,
    //   typedSignature,
    // });
    // Get the current signature data (base64) from the temp signature state
    const currentSignatureData = tempSignature;

    // Handle signature vs initials based on widget type
    if (widgetType === "initials") {
      // For initials, save base64 data to myInitial state
      if (currentSignatureData) {
        setMyInitial(currentSignatureData);
        // console.log("Initials base64 data saved:", currentSignatureData);
      }
    } else {
      // For signature, save base64 data to signature state
      if (currentSignatureData) {
        setSignature(currentSignatureData);
        // console.log("Signature base64 data saved:", currentSignatureData);
      }
    }

    // Clear temp signature after saving
    setTempSignature(null);
    setIsSignPad(false);
    setIsImageSelect(false);
    setImage(null);
  };

  // Function to save all data and print payload
  const saveAllData = () => {
    const payload = {
      signature: signature,
      initials: myInitial,
      timestamp: new Date().toISOString(),
      user: "User", // Mock user
    };

    // console.log("=== SAVE PAYLOAD ===");
    // console.log("Full Payload:", payload);
    // console.log("Signature Data:", signature);
    // console.log("Initials Data:", myInitial);
    // console.log("===================");4
    updateProfile({
      signature_base64: signature,
      initials_base64: myInitial,
    });
    // Here you would typically send to API
    // For now, just log the payload
    toast.success("Signature and initials saved successfully");
  };

  // Function to save image
  const saveImage = () => {
    // console.log("Image saved:", image);

    // Handle signature vs initials based on widget type
    if (widgetType === "initials") {
      // For initials, save base64 image data to myInitial state
      if (image && image.src) {
        setMyInitial(image.src);
        toast.success("Initials image saved successfully");
        // console.log("Initials base64 image data saved:", image.src);
      }
    } else {
      // For signature, save base64 image data to signature state
      if (image && image.src) {
        setSignature(image.src);
        toast.success("Signature image saved successfully");
        // console.log("Signature base64 image data saved:", image.src);
      }
    }

    setIsSignPad(false);
    setIsImageSelect(false);
    setImage(null);
  };

  // Function to handle signature change from SignPad
  const handleSignatureChange = (signatureData) => {
    setTempSignature(signatureData);
    // console.log("Temp signature data updated:", signatureData);
  };

  // Function to open signature pad
  const openSignaturePad = (type = "signature") => {
    setWidgetType(type);
    setIsSignPad(true);
    setIsStamp(false);
    setIsImageSelect(false);

    // Clear temp signature when opening
    setTempSignature(null);

    // Set signature types based on whether it's signature or initials
    if (type === "initials") {
      // For initials, only allow typing (no drawing)
      setSignatureType([{ name: "typed", enabled: true }]);
      setIsInitial(true);
    } else {
      // For signature, allow both drawing and typing
      setSignatureType([
        { name: "draw", enabled: true },
        { name: "typed", enabled: true },
      ]);
      setIsInitial(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Signature</h1>
              <p className="text-gray-600 mt-1">
                Create and manage your digital signatures
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={saveAllData}
                className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
                  />
                </svg>
                Save All
              </button>
            </div>
          </div>
        </div>

        {/* Signature Options */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Signature Section */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Signature
            </h2>
            <div className="space-y-4">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                {signature ? (
                  <div className="space-y-4">
                    <img
                      src={signature}
                      alt="Signature"
                      className="mx-auto max-w-full max-h-32 object-contain"
                    />
                    <p className="text-sm text-gray-500">Your signature</p>
                    <button
                      onClick={() => setSignature(null)}
                      className="text-red-600 hover:text-red-700 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div className="text-gray-500">
                    <PencilIcon className="w-8 h-8 mx-auto mb-2" />
                    <p>No signature created yet</p>
                    <div className="mt-2 space-x-2">
                      <button
                        onClick={() => openSignaturePad("signature")}
                        className="text-blue-600 hover:text-blue-700 text-sm"
                      >
                        Create signature
                      </button>
                      <span className="text-gray-400">or</span>
                      <button
                        onClick={() => signatureImageRef.current.click()}
                        className="text-green-600 hover:text-green-700 text-sm"
                      >
                        Upload image
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Initials Section */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Initials
            </h2>
            <div className="space-y-4">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                {myInitial ? (
                  <div className="space-y-4">
                    <img
                      src={myInitial}
                      alt="Initials"
                      className="mx-auto max-w-full max-h-32 object-contain"
                    />
                    <p className="text-sm text-gray-500">Your initials</p>
                    <button
                      onClick={() => setMyInitial(null)}
                      className="text-red-600 hover:text-red-700 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div className="text-gray-500">
                    <PencilIcon className="w-8 h-8 mx-auto mb-2" />
                    <p>No initials created yet</p>
                    <div className="mt-2 space-x-2">
                      <button
                        onClick={() => openSignaturePad("initials")}
                        className="text-blue-600 hover:text-blue-700 text-sm"
                      >
                        Create initials
                      </button>
                      <span className="text-gray-400">or</span>
                      <button
                        onClick={() => initialsImageRef.current.click()}
                        className="text-green-600 hover:text-green-700 text-sm"
                      >
                        Upload image
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Hidden file inputs */}
        <input
          type="file"
          ref={signatureImageRef}
          onChange={onSignatureImageChange}
          accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
          style={{ display: "none" }}
        />
        <input
          type="file"
          ref={initialsImageRef}
          onChange={onInitialsImageChange}
          accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
          style={{ display: "none" }}
        />

        {/* Instructions */}
        <div className="bg-white rounded-lg shadow-sm p-6 mt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            How to use
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <PencilIcon className="w-6 h-6 text-blue-600" />
              </div>
              <h4 className="font-medium text-gray-900 mb-2">
                Draw (Signature only)
              </h4>
              <p className="text-sm text-gray-600">
                Use your mouse or touch to draw your signature
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg
                  className="w-6 h-6 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
              </div>
              <h4 className="font-medium text-gray-900 mb-2">Type</h4>
              <p className="text-sm text-gray-600">
                Type your name/initials and choose from different fonts
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg
                  className="w-6 h-6 text-orange-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
              </div>
              <h4 className="font-medium text-gray-900 mb-2">Upload Image</h4>
              <p className="text-sm text-gray-600">
                Upload an image file (PNG, JPEG, GIF, WebP)
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg
                  className="w-6 h-6 text-purple-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h4 className="font-medium text-gray-900 mb-2">Save</h4>
              <p className="text-sm text-gray-600">
                Save your signature/initials for future use
              </p>
            </div>
          </div>
          <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              <strong>Note:</strong> For initials, only typing and image upload
              are available. Drawing option is only available for full
              signatures.
            </p>
          </div>
        </div>
      </div>

      {/* Signature Pad Modal */}
      <SignPad
        saveSignCheckbox={saveSignCheckbox}
        setSaveSignCheckbox={setSaveSignCheckbox}
        signatureTypes={signatureType}
        isSignPad={isSignPad}
        isStamp={isStamp}
        setIsImageSelect={setIsImageSelect}
        setIsSignPad={setIsSignPad}
        setImage={setImage}
        isImageSelect={isImageSelect}
        imageRef={imageRef}
        onImageChange={onImageChange}
        setSignature={handleSignatureChange}
        image={image}
        onSaveImage={saveImage}
        onSaveSign={saveSign}
        defaultSign={defaultSignImg}
        myInitial={myInitial}
        setDefaultSignImg={setDefaultSignImg}
        setMyInitial={setMyInitial}
        isInitial={isInitial}
        setIsInitial={setIsInitial}
        setIsStamp={setIsStamp}
        currWidgetsDetails={currWidgetsDetails}
        setCurrWidgetsDetails={setCurrWidgetsDetails}
        setIsAutoSign={setIsAutoSign}
        isAutoSign={isAutoSign}
        userDetails={{ name: "User" }} // Mock user details
      />
    </div>
  );
};

export default Signature;
